/**
 * MemberOfficesDialog — every office this person holds across the platform.
 * A church admin sees where they serve; contact details from other modules
 * stay in those modules.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Loader2, Church, GraduationCap, CalendarDays, UserCog, Layers } from 'lucide-react';

const ICONS = {
  Church, Schools: GraduationCap, Events: CalendarDays,
  'Admin console': UserCog, 'Member area': Layers,
};

export const MemberOfficesDialog = ({ churchId, member, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['member-offices', churchId, member?.id],
    queryFn: async () => (await api.get(
      `/church/${churchId}/members/${member.id}/offices`)).data,
    enabled: !!member,
  });

  if (!member) return null;
  const offices = data?.offices || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="member-offices-dialog">
        <DialogHeader>
          <DialogTitle>{member.first_name} {member.last_name}</DialogTitle>
          <DialogDescription>
            Every office this person holds across the platform — churches, schools, events and
            logins. One person, however many hats.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        ) : offices.length === 0 ? (
          <p className="py-6 text-sm text-slate-500" data-testid="member-offices-empty">
            Nothing else on record — this roll is the only place they appear.
          </p>
        ) : (
          <div className="space-y-2" data-testid="member-offices-list">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data.by_module || {}).map(([mod, n]) => (
                <Badge key={mod} variant="outline" className="text-[10px]">
                  {mod} · {n}
                </Badge>
              ))}
            </div>
            <div className="divide-y rounded-lg border border-slate-200">
              {offices.map((o, i) => {
                const Icon = ICONS[o.module] || Layers;
                return (
                  <div key={`${o.collection}-${o.record_id}-${i}`}
                       className="flex items-center gap-3 p-2.5"
                       data-testid={`member-office-${i}`}>
                    <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-slate-800 truncate">
                        {o.role ? o.role : o.kind}
                        {o.unit_name ? ` · ${o.unit_name}` : ''}
                      </p>
                      <p className="text-xs text-slate-500">
                        {o.module} · {o.kind}{o.since ? ` · since ${o.since}` : ''}
                      </p>
                    </div>
                    {o.status && (
                      <Badge variant="outline" className="text-[10px] capitalize shrink-0">
                        {o.status}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MemberOfficesDialog;
